import type { ApiService } from './api/apiService';
import { FakeApi } from './api/fakeApi';
import { HttpApi } from './api/httpApi';
import { DefaultAuthRepository } from './repository/authRepository';
import { DefaultReservationRepository } from './repository/reservationRepository';
import { DefaultRestaurantRepository } from './repository/restaurantRepository';
import type { ReservationRepository, RestaurantRepository } from './repository/repositories';

/**
 * Contenedor de dependencias, equivalente a los módulos de Hilt del cliente Android.
 *
 * Es el **único** punto que sabe si detrás hay un backend real o el simulador. Cambiar de uno a
 * otro es cambiar `VITE_USE_FAKE_BACKEND` en el `.env`: ni los repositorios, ni el estado, ni
 * las pantallas cambian una línea.
 */
export interface Container {
  readonly api: ApiService;
  readonly restaurants: RestaurantRepository;
  readonly reservations: ReservationRepository;
  readonly auth: DefaultAuthRepository;
}

const USE_FAKE_BACKEND = import.meta.env.VITE_USE_FAKE_BACKEND !== 'false';
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

export function createContainer(apiOverride?: ApiService): Container {
  // El repositorio de sesión custodia el token, y el cliente HTTP lo lee en cada petición; por
  // eso se construye antes que la API real y recibe una referencia perezosa.
  let auth: DefaultAuthRepository | null = null;
  const api =
    apiOverride ??
    (USE_FAKE_BACKEND ? new FakeApi() : new HttpApi(BASE_URL, () => auth?.accessToken() ?? null));

  auth = new DefaultAuthRepository(api);
  return {
    api,
    restaurants: new DefaultRestaurantRepository(api),
    reservations: new DefaultReservationRepository(api),
    auth,
  };
}

export const usingFakeBackend = USE_FAKE_BACKEND;
